import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} panel
 * @property {HTMLElement} backdrop
 * @property {HTMLElement} [inner]
 * @property {HTMLElement} [subpanel]
 */

/** Matches the slide in snippets/unified-mega-menu.liquid. */
const SUBMENU_TRANSITION_MS = 250;

/** A sub-menu column always fits at least about four links, however short the main screen. */
const SUBMENU_MIN_LIST_HEIGHT = 140;

/**
 * Single hamburger trigger + one full-width panel (snippets/unified-mega-menu.liquid).
 * Toggle-open/close semantics are hand-rolled rather than built on <theme-drawer> — that
 * component slides a side panel in and out; this one fades/drops a full-width panel below the
 * header, a different enough shape that reusing it would fight its assumptions more than it
 * would save. Uses the same --animation-speed / --animation-easing tokens for a matching feel.
 *
 * @extends {Component<Refs>}
 */
class UnifiedMegaMenu extends Component {
  requiredRefs = ['trigger', 'panel', 'backdrop'];

  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #localizationPanel = null;
  /** @type {HTMLElement | null} */
  #localizationButton = null;
  /** @type {(HTMLElement & { hidePanel?: () => void }) | null} */
  #localizationComponent = null;
  /** @type {MutationObserver | null} */
  #localizationObserver = null;

  /** @type {HTMLElement | null} The sub-menu screen currently shown. */
  #openSubmenu = null;
  /** @type {HTMLElement | null} The "Shop other" link that opened it. */
  #submenuToggle = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('click', this.#onOutsideClick, { signal });
    document.addEventListener('shopify:block:select', this.#onEditorBlockSelect, { signal });
    this.addEventListener('click', this.#onSubmenuClick, { signal });
    this.addEventListener('keydown', this.#onSubmenuKeyDown, { signal });
    window.addEventListener('resize', this.#onResize, { passive: true, signal });

    this.#mountSubmenus();

    // dropdown-localization-component is its own Component, so its "panel"/"button" refs live in
    // its own ref scope, not this.refs — hence plain querySelector here instead.
    this.#localizationPanel = this.querySelector('.unified-mega-menu__localization-panel');
    this.#localizationButton = this.querySelector('.unified-mega-menu__localization-button');
    this.#localizationComponent = this.querySelector('dropdown-localization-component');

    if (this.#localizationPanel) {
      this.#localizationObserver = new MutationObserver(this.#onLocalizationPanelToggle);
      this.#localizationObserver.observe(this.#localizationPanel, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#localizationObserver?.disconnect();
  }

  /**
   * The localization popover lives inside .unified-mega-menu__panel, which uses `clip-path` for
   * its open/close curtain animation. clip-path clips every descendant regardless of position
   * scheme — fixed included — so without this the popover has to fit inside the panel's own
   * bounds (see its CSS in unified-mega-menu.liquid). The native Popover API promotes it to the
   * top layer, escaping that clipping entirely, while leaving it exactly where it is in the DOM
   * tree — unlike moving the node itself, this keeps
   * dropdown-localization-component's own click-outside detection working
   * (assets/localization.js's isClickedOutside uses `element.contains()`, a DOM-tree check a
   * real reparent would break: a click inside a moved-out panel would register as "outside" the
   * component and close it immediately).
   *
   * assets/localization.js still owns all the actual open/close logic (toggle button, Escape,
   * click-outside) — this only mirrors its resulting `hidden` state onto the Popover API so the
   * already-open/closed panel also gets promoted/demoted from the top layer.
   */
  #onLocalizationPanelToggle = () => {
    const panel = this.#localizationPanel;
    if (!panel || typeof panel.showPopover !== 'function') return;

    const shouldShow = !panel.hasAttribute('hidden');
    const isShowing = panel.matches(':popover-open');

    if (shouldShow && !isShowing) {
      panel.showPopover();
      this.#positionLocalizationPanel();
    } else if (!shouldShow && isShowing) {
      panel.hidePopover();
    }
  };

  /**
   * The top layer isn't positioned relative to dropdown-localization-component anymore (the
   * left-bound/right-bound CSS in header.liquid assumes a normal `position: absolute` ancestor),
   * so it needs explicit fixed coordinates computed from the trigger button instead — then
   * clamped to the viewport's right edge so it can't spill off-screen when the trigger sits near
   * the sidebar's right side.
   */
  #positionLocalizationPanel() {
    const panel = this.#localizationPanel;
    const button = this.#localizationButton;
    if (!panel || !button) return;

    const buttonRect = button.getBoundingClientRect();
    panel.style.position = 'fixed';
    // The UA popover stylesheet sets `inset: 0; margin: auto;` (its default centering) — all
    // four inset longhands need an explicit value here, not just top/left, or the leftover
    // `bottom: 0`/`right: 0` stretch/reposition the box against these.
    panel.style.top = `${buttonRect.bottom + 10}px`;
    panel.style.left = `${buttonRect.left}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
    panel.style.margin = '0';

    const panelRect = panel.getBoundingClientRect();
    const overflowRight = panelRect.right - document.documentElement.clientWidth;
    if (overflowRight > 0) {
      panel.style.left = `${Math.max(8, buttonRect.left - overflowRight)}px`;
    }
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open() {
    if (this.isOpen) return;
    // A sub-menu left open when the menu last closed: back to the main screen without sliding.
    this.closeSubmenu({ instant: true, restoreFocus: false });
    this.setAttribute('open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.refs.panel.removeAttribute('inert');
    this.refs.backdrop.hidden = false;
  }

  close = () => {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('inert', '');
    this.refs.backdrop.hidden = true;

    // Otherwise a still-open localization popover would be left floating in the top layer,
    // visible above nothing, once the (now-inert) panel curtain-closes beneath it. Going through
    // their own hidePanel() — rather than calling hidePopover() here directly — keeps
    // assets/localization.js's own `hidden`/aria-expanded state in sync too; it sets `hidden`,
    // which #onLocalizationPanelToggle picks up to close the popover itself.
    this.#localizationComponent?.hidePanel?.();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    // Escape steps back out of a sub-menu first; a second Escape closes the menu.
    if (this.#openSubmenu) {
      this.closeSubmenu();
      return;
    }
    this.close();
    this.refs.trigger.focus();
  };

  /**
   * Sub-menu screens are rendered hidden inside their "Shop other" link block
   * (blocks/_mega-menu-text-link.liquid), which sits in the main content that slides away. They
   * are moved into the subpanel so they stay put while it slides, and their links become buttons.
   * Moving keeps their editor attributes, so blocks inside them stay selectable in the editor,
   * which re-renders the whole section (and reconnects this element) after every change.
   */
  #mountSubmenus() {
    const { inner, subpanel } = this.refs;
    if (!inner || !subpanel) return;

    for (const submenu of inner.querySelectorAll('[data-submenu]')) {
      subpanel.append(submenu);
    }

    for (const toggle of inner.querySelectorAll('[data-submenu-toggle]')) {
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * @param {HTMLElement} toggle - The "Shop other" link whose sub-menu to show.
   * @param {{ focus?: boolean }} [options]
   */
  openSubmenu(toggle, { focus = true } = {}) {
    const { panel, inner } = this.refs;
    const id = toggle.getAttribute('aria-controls');
    const submenu = id ? document.getElementById(id) : null;
    if (!submenu || !inner) return;

    if (this.#openSubmenu && this.#openSubmenu !== submenu) {
      this.#submenuToggle?.setAttribute('aria-expanded', 'false');
    }

    // Every other screen, including one still sliding out after a quick back-then-open.
    for (const other of this.refs.subpanel?.querySelectorAll('[data-submenu]') ?? []) {
      if (other !== submenu && other instanceof HTMLElement) other.hidden = true;
    }

    // The language/currency popover sits in the top layer and would float over the sub-menu.
    this.#localizationComponent?.hidePanel?.();

    submenu.hidden = false;
    this.#openSubmenu = submenu;
    this.#submenuToggle = toggle;
    toggle.setAttribute('aria-expanded', 'true');
    inner.inert = true;

    this.#fitSubmenu();
    panel.setAttribute('data-submenu-open', '');

    if (focus) submenu.querySelector('[data-submenu-back]')?.focus({ preventScroll: true });
  }

  /**
   * Slides back to the main screen. The sub-menu is hidden only after the slide, and never
   * removed: the document click listener checks `this.contains()` on the back button's click.
   * @param {{ instant?: boolean, restoreFocus?: boolean }} [options]
   */
  closeSubmenu({ instant = false, restoreFocus = true } = {}) {
    const submenu = this.#openSubmenu;
    if (!submenu) return;

    const { panel, inner } = this.refs;
    const toggle = this.#submenuToggle;
    this.#openSubmenu = null;
    this.#submenuToggle = null;

    if (instant) panel.setAttribute('data-submenu-instant', '');
    panel.removeAttribute('data-submenu-open');
    if (inner) inner.inert = false;
    toggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle?.focus({ preventScroll: true });

    const finish = () => {
      if (this.#openSubmenu !== submenu) submenu.hidden = true;
      if (!this.#openSubmenu) panel.style.minHeight = '';
    };

    if (instant || prefersReducedMotion()) {
      finish();
      // Apply the reset with transitions off (the reflow), then turn them back on next frame.
      void panel.offsetWidth;
      requestAnimationFrame(() => panel.removeAttribute('data-submenu-instant'));
    } else {
      setTimeout(finish, SUBMENU_TRANSITION_MS);
    }
  }

  /**
   * Lets the open sub-menu's links run down as far as the main screen is tall before wrapping
   * into a new column, so the panel keeps its height. Only a main screen too short for
   * SUBMENU_MIN_LIST_HEIGHT stretches the panel.
   */
  #fitSubmenu() {
    const submenu = this.#openSubmenu;
    const { panel, inner } = this.refs;
    const list = submenu?.querySelector('.unified-mega-menu__submenu-list');
    if (!submenu || !inner || !(list instanceof HTMLElement)) return;

    list.style.removeProperty('--submenu-list-max-height');
    const chrome = submenu.offsetHeight - list.offsetHeight;
    const listHeight = Math.max(inner.offsetHeight - chrome, SUBMENU_MIN_LIST_HEIGHT);
    list.style.setProperty('--submenu-list-max-height', `${listHeight}px`);

    panel.style.minHeight = submenu.offsetHeight > inner.offsetHeight ? `${submenu.offsetHeight}px` : '';
  }

  #onResize = () => {
    if (this.#openSubmenu) this.#fitSubmenu();
  };

  /** @param {MouseEvent} event */
  #onSubmenuClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-submenu-toggle]');
    if (toggle instanceof HTMLElement && this.refs.inner?.contains(toggle)) {
      event.preventDefault();
      this.openSubmenu(toggle);
      return;
    }

    if (event.target.closest('[data-submenu-back]')) {
      this.closeSubmenu();
    }
  };

  /**
   * The toggles are links given role="button", which Enter already activates; Space needs this.
   * @param {KeyboardEvent} event
   */
  #onSubmenuKeyDown = (event) => {
    if (event.key !== ' ' || !(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-submenu-toggle]');
    if (!(toggle instanceof HTMLElement) || !this.refs.inner?.contains(toggle)) return;
    event.preventDefault();
    this.openSubmenu(toggle);
  };

  /**
   * Selecting a block inside a sub-menu in the theme editor opens the menu on that sub-menu, and
   * selecting any other block in the menu opens the menu on its main screen.
   * @param {Event} event
   */
  #onEditorBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    this.open();

    const submenu = event.target.closest('[data-submenu]');
    if (!submenu) {
      this.closeSubmenu({ instant: true, restoreFocus: false });
      return;
    }
    const toggle = this.querySelector(`[aria-controls="${submenu.id}"]`);
    if (toggle instanceof HTMLElement) this.openSubmenu(toggle, { focus: false });
  };

  /** @param {MouseEvent} event */
  #onOutsideClick = (event) => {
    if (!this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.close();
  };
}

if (!customElements.get('unified-mega-menu')) {
  customElements.define('unified-mega-menu', UnifiedMegaMenu);
}
